import { JSX } from 'react';

import { BreadcrumbsContainer } from 'shared/components/Breadcrumbs';
import { ListingCardsContainer } from 'shared/components/Cards';
import { SimilarOffersCardsContainer } from 'shared/components/OtherCards';
import { SEOBlock } from 'shared/components/SEOBlock';
import { useApplicationContext } from 'shared/context/ApplicationContext';
import { ListingContextProvider } from 'shared/context/ListingContext';
import { useDisablePageScroll } from 'shared/hooks/useDisablePageScroll';
import { useBrowserPageIsPageReloadingSelector } from 'shared/store/clientSide/slices/browserPage';
import { useGetOffersQuery } from 'shared/store/serverSide/commercial-search-offers/desktop/v2/offers/get-offers/query';
import { getIsBrowserBuild } from 'shared/utils/isBuildType';
import { memoWithDisplayName } from 'shared/utils/memoWithDisplayName';

import { ListingLoadErrorView } from '../ListingLoadErrorView';
import { ListingLoadingView } from '../ListingLoadingView';
import { ListingNoCards } from '../ListingNoCards';
import { ListingPaginationContainer } from '../Pagination';
import { ToLegacyListingContainer } from '../ToLegacyListing';
import { VisitedOffersAnalytics } from '../VisitedOffersAnalytics';

import { ListingLayout } from './ListingLayout';

export const ListingContainer = memoWithDisplayName(() => {
  const { data, isFetching, refetch, status } = useGetOffersQuery();

  const isPageReloading = useBrowserPageIsPageReloadingSelector();

  const showSpinner = (isFetching || isPageReloading) && getIsBrowserBuild();

  useDisablePageScroll(showSpinner);

  /**
   * @todo Удалить фича-тоггл isShowToLegacyListingBanner
   * @description Данный функционал появился в задаче CD-208441, будет удалён в задаче CD-208720
   */
  const { config } = useApplicationContext();
  const isShowToLegacyListingBanner = config.getStrict<boolean>('Commercial.ShowToLegacyListingBanner.Enabled');

  switch (status) {
    case 'loading':
      return <ListingLoadingView />;

    case 'error':
      return (
        <ListingLayout
          banners={null}
          cards={null}
          isFetching={showSpinner}
          navigation={null}
          pagination={null}
          renderNoCards={(): JSX.Element => <ListingLoadErrorView isLoading={isFetching} onRetry={refetch} />}
          seo={null}
          similarCards={null}
        />
      );

    case 'success': {
      const { offers, similarOffers } = data;

      return (
        <ListingContextProvider value={data}>
          <ListingLayout
            banners={null}
            cards={!!offers.length && <ListingCardsContainer />}
            isFetching={showSpinner}
            navigation={<BreadcrumbsContainer />}
            pagination={<ListingPaginationContainer />}
            renderNoCards={(): JSX.Element => <ListingNoCards />}
            seo={<SEOBlock />}
            similarCards={!!similarOffers && <SimilarOffersCardsContainer />}
            /**
             * @todo Удалить пропсу (и фича-тоггл)
             * @description Данный функционал появился в задаче CD-162894, будет удалён в задаче CD-193764
             */
            toLegacyListing={isShowToLegacyListingBanner && <ToLegacyListingContainer />}
          />
          <VisitedOffersAnalytics isFetchingOffers={isFetching} offers={offers} />
        </ListingContextProvider>
      );
    }
  }
}, 'ListingContainer');
